import { useRef, useEffect, useCallback } from 'react';
import YearProgress from '../YearProgress';
import {
  MONTH_LABEL_WIDTH,
  HEADER_HEIGHT,
  CELL_WIDTH,
  CELL_MIN_HEIGHT
} from '../../constants/layout';

/**
 * Component that renders sticky header and month label overlays.
 * Manages scroll synchronization to keep headers visible while scrolling.
 */
function StickyOverlays({ containerRef, scaleFactor, monthHeights, year, yearData, columnHeaders, isDragging = false }) {
  const stickyCornerRef = useRef(null);
  const stickyHeadersRef = useRef(null);
  const stickyMonthsRef = useRef(null);

  // Update sticky label positions
  const updateStickyPositions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Skip layout-forcing getBoundingClientRect during drag to prevent blur
    if (isDragging) return;
    
    const containerRect = container.getBoundingClientRect();
    const baseLeft = containerRect.left;
    const baseTop = containerRect.top;
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;
    const containerWidth = containerRect.width - 2;
    const containerHeight = containerRect.height - 2;
    
    const scaledMonthLabelWidth = MONTH_LABEL_WIDTH * scaleFactor;
    const scaledHeaderHeight = HEADER_HEIGHT * scaleFactor;
    
    if (stickyCornerRef.current) {
      stickyCornerRef.current.style.left = `${baseLeft}px`;
      stickyCornerRef.current.style.top = `${baseTop}px`;
      stickyCornerRef.current.style.width = `${scaledMonthLabelWidth}px`;
      stickyCornerRef.current.style.height = `${scaledHeaderHeight}px`;
    }
    
    if (stickyHeadersRef.current) {
      stickyHeadersRef.current.style.left = `${baseLeft + scaledMonthLabelWidth}px`;
      stickyHeadersRef.current.style.top = `${baseTop}px`;
      stickyHeadersRef.current.style.width = `${containerWidth - scaledMonthLabelWidth}px`;
      stickyHeadersRef.current.style.height = `${scaledHeaderHeight}px`;
      
      const headersContent = stickyHeadersRef.current.querySelector('.calendar__sticky-headers-content');
      if (headersContent) {
        headersContent.style.transform = `translateX(${-scrollLeft}px)`;
      }
      
      const headerCells = stickyHeadersRef.current.querySelectorAll('.calendar__header-cell');
      headerCells.forEach(cell => {
        cell.style.width = `${CELL_WIDTH * scaleFactor}px`;
        cell.style.height = `${scaledHeaderHeight}px`;
        cell.style.fontSize = `${9 * scaleFactor}px`;
      });
    }
    
    if (stickyMonthsRef.current) {
      stickyMonthsRef.current.style.left = `${baseLeft}px`;
      stickyMonthsRef.current.style.top = `${baseTop + scaledHeaderHeight}px`;
      stickyMonthsRef.current.style.width = `${scaledMonthLabelWidth}px`;
      stickyMonthsRef.current.style.height = `${containerHeight - scaledHeaderHeight}px`;
      
      const monthsContent = stickyMonthsRef.current.querySelector('.calendar__sticky-months-content');
      if (monthsContent) {
        monthsContent.style.transform = `translateY(${-scrollTop}px)`;
      }
      
      const monthLabels = stickyMonthsRef.current.querySelectorAll('.calendar__sticky-month-label');
      monthLabels.forEach((label, index) => {
        label.style.width = `${scaledMonthLabelWidth}px`;
        // monthHeights contain base (unscaled) values, need to scale for display
        const height = (monthHeights[index] || CELL_MIN_HEIGHT) * scaleFactor;
        label.style.height = `${height}px`;
        label.style.fontSize = `${11 * scaleFactor}px`;
        label.style.paddingRight = `${8 * scaleFactor}px`;
      });
    }
  }, [containerRef, scaleFactor, monthHeights, isDragging]);

  // Update positions on scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    let rafId = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        updateStickyPositions();
        rafId = null;
      });
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [containerRef, updateStickyPositions]);

  // Update positions on resize or zoom change
  // Changed from useLayoutEffect to useEffect to prevent layout thrashing during drag
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Defer initial positioning to avoid blocking paint
    let rafId;
    rafId = requestAnimationFrame(updateStickyPositions);
    
    const resizeObserver = new ResizeObserver(() => {
      updateStickyPositions();
    });
    
    resizeObserver.observe(container);
    
    const handleResize = () => {
      updateStickyPositions();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, updateStickyPositions, scaleFactor, monthHeights]);

  return (
    <>
      {/* Sticky corner with year progress */}
      <div 
        ref={stickyCornerRef}
        className="calendar__sticky-corner"
        style={{
          width: `${MONTH_LABEL_WIDTH * scaleFactor}px`,
          height: `${HEADER_HEIGHT * scaleFactor}px`
        }}
      >
        <YearProgress year={year} />
      </div>
      
      {/* Sticky day-of-week headers */}
      <div 
        ref={stickyHeadersRef}
        className="calendar__sticky-headers"
        style={{
          height: `${HEADER_HEIGHT * scaleFactor}px`
        }}
      >
        <div className="calendar__sticky-headers-content" style={{ display: 'flex' }}>
          {columnHeaders.map((header, index) => (
            <div
              key={index}
              className={`calendar__header-cell ${header.isWeekend ? 'calendar__header-cell--weekend' : ''}`}
              style={{
                width: `${CELL_WIDTH * scaleFactor}px`,
                height: `${HEADER_HEIGHT * scaleFactor}px`,
                fontSize: `${9 * scaleFactor}px`
              }}
            >
              {header.label}
            </div>
          ))}
        </div>
      </div>
      
      {/* Sticky month labels */}
      <div 
        ref={stickyMonthsRef}
        className="calendar__sticky-months"
        style={{
          width: `${MONTH_LABEL_WIDTH * scaleFactor}px`
        }}
      >
        <div className="calendar__sticky-months-content" style={{ display: 'flex', flexDirection: 'column' }}>
          {yearData.map((month, index) => (
            <div 
              key={month.index} 
              className="calendar__sticky-month-label"
              style={{
                width: `${MONTH_LABEL_WIDTH * scaleFactor}px`,
                height: `${(monthHeights[index] || CELL_MIN_HEIGHT) * scaleFactor}px`,
                fontSize: `${11 * scaleFactor}px`,
                paddingRight: `${8 * scaleFactor}px`
              }}
            >
              {month.shortName}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default StickyOverlays;
